/**
 * THE region-selection resolver.
 *
 * Every path that lets a shopper choose where they are shopping — the globe,
 * the flag tiles, a Google-resolved place, the detect chip, a click on the
 * country map, the header, the tutorial — ends here. One commit, one order of
 * side effects, one URL shape. Before this existed, three of those paths wrote
 * a `UserLocation` and three did not, so choosing "Canada" from the globe left
 * the header badge and the AI with nothing while choosing "Quebec" from the map
 * worked: the same click, two different outcomes, depending on which widget you
 * happened to use.
 *
 * The split is deliberate:
 *  - `buildSelectionPayload` is PURE. It answers "what should be stored, what
 *    should the URL be" with no DOM, no storage, no navigation, so the URL and
 *    payload rules are unit-testable.
 *  - `commitRegionSelection` performs the side effects, in the exact order the
 *    /region page has always performed them.
 *
 * Two invariants the CALL SITES own, and that this module preserves rather than
 * re-derives:
 *  - coordinates ride along only onto the subregion they actually fall in;
 *  - `subregion` is always an admin-1 code or the whole-country sentinel, never
 *    an empty string.
 *
 * Registry-free on purpose: nothing here imports the 48KB admin-1 registry, so
 * importing the resolver never inflates a bundle. Callers that need canonical
 * codes resolve them where the registry is already paid for.
 */

import Cookies from 'js-cookie';
import { countryLanguages, getDefaultLocaleForRegion, Locale } from 'shared/src/types/languages.type';
import { getSubregionLocale } from '@/lib/subregion-locale';
import { getCountryDisplayName, getRegionForCountryCode } from 'shared/src/types/region.type';
import { resolveCrossRegionUrl } from '@/lib/runtime/app-origin';
import { encodeLocationParam } from '@/lib/location-handoff';
import { setUserLocation, UserLocation } from '@/lib/user-location';
import {
  DEFAULT_REGION_PRIORITY,
  encodeSubregionPrefCookie,
  isRegionPriority,
  parseSubregionPrefCookie,
  RegionPrefSource,
  RegionPriority,
  SUBREGION_PREF_COOKIE,
  SUBREGION_PREF_COOKIE_DAYS,
  SubregionPref,
  WHOLE_COUNTRY_CODE,
} from '@/lib/subregion-pref';

/**
 * Which surface the shopper used. Carried for analytics and for future
 * per-surface rules; it never changes what gets stored today.
 */
export type RegionSelectionSource =
  | 'globe'
  | 'flag-tile'
  | 'places'
  | 'detect-chip'
  | 'map-click'
  | 'header'
  | 'tutorial';

export interface RegionSelection {
  /** Internal country code — 'CA', 'US', 'UK' (never 'GB'). */
  countryCode: string;
  /** Admin-1 code, or `WHOLE_COUNTRY_CODE`. Never empty — see module note. */
  subregion: string;
  /** Display label for the stored location: the city, else the area, else the country. */
  city?: string | null;
  /** Only ever the coordinates of the chosen subregion — see module note. */
  coordinates?: { lat: number; lng: number } | null;
  /** Stated ordering. Omitted ⇒ `country-first` ⇒ today's behavior. */
  regionPriority?: RegionPriority;
  /**
   * Provenance. Every surface in `RegionSelectionSource` is a click, so this
   * defaults to `explicit`; pass `detected` for anything auto-applied.
   */
  regionSource?: RegionPrefSource;
  source: RegionSelectionSource;
}

/** Everything the pure builder needs from the page around it. */
export interface RegionSelectionContext {
  /**
   * Region enum name ("Canada", "USA", "UK"). Derived from `countryCode` when
   * omitted; pass it when the caller already holds it.
   */
  regionName?: string | null;
  /** Path on the CURRENT locale to carry over to the target locale. */
  returnUrl?: string | null;
  /** In-flight search query, preserved across the hop. */
  searchQuery?: string | null;
  /** Tutorial mode: appends `tutorial=step2` and ignores `returnUrl`. */
  tutorialMode?: boolean;
  /** Label to store as `city` when the selection carries none. */
  fallbackLabel?: string | null;
  /** Dev navigates same-origin with the router; prod resolves the region apex. */
  isDev?: boolean;
  /** Injectable clock, so payload tests are deterministic. */
  now?: number;
}

/** The commit additionally needs a way to navigate. */
export interface RegionCommitContext extends RegionSelectionContext {
  /** `router.push` from `next/navigation`. */
  push: (url: string) => void;
}

export interface RegionSelectionPayload {
  /** Exactly what gets written to localStorage / synced to the profile. */
  location: UserLocation;
  /** Value of the `loc` query param (unencoded — `URLSearchParams` escapes it). */
  locParam: string;
  /** Locale path the target origin should be pinned to, e.g. 'fr-CA'. */
  localeToSet: string;
  /** Where to navigate: a path in dev / on the app host, an absolute apex otherwise. */
  targetUrl: string;
}

/** TTL, in minutes, of the "don't re-ask me where I am" flag. Unchanged. */
const REGION_PROMPT_DISMISSED_TTL_MINUTES = 30;

/** True when the value means "the whole country", including the empty case. */
function isWholeCountryCode(region: string | null | undefined): boolean {
  const trimmed = region?.trim();
  return !trimmed || trimmed === WHOLE_COUNTRY_CODE;
}

/**
 * Replace the locale segment of a path, or prepend one. Lifted verbatim from
 * the /region page so the URL a country switch produces cannot drift between
 * the paths that used to each own a copy.
 */
export function convertPathToLocale(currentPath: string | null | undefined, newLocale: string): string {
  if (!currentPath) return `/${newLocale}`;

  const pathParts = currentPath.split('/');
  if (pathParts.length > 1 && pathParts[1]) {
    // A locale segment contains a dash and is at least 4 characters ('fr-CA').
    if (pathParts[1].includes('-') && pathParts[1].length >= 4) {
      pathParts[1] = newLocale;
      return pathParts.join('/');
    }
  }

  return `/${newLocale}${currentPath}`;
}

/**
 * PURE. What a selection means: the location to store, the handoff param, the
 * locale to pin, and where to go.
 *
 * `null` when the country has no deployed locale — the /region page has always
 * returned early there rather than navigating somewhere that does not exist.
 */
export function buildSelectionPayload(
  selection: RegionSelection,
  context: RegionSelectionContext = {},
): RegionSelectionPayload | null {
  const countryCode = selection.countryCode.trim().toUpperCase();
  const regionName = context.regionName || getRegionForCountryCode(countryCode);
  if (!regionName) return null;

  const countryDefaultLocale = getDefaultLocaleForRegion(regionName);
  if (!countryDefaultLocale) return null;

  const subregion = selection.subregion?.trim() || WHOLE_COUNTRY_CODE;

  // Choosing Québec chooses French. A subregion with its own language pins that
  // language instead of the country default — but only when we actually deploy
  // the locale AND it belongs to this country, so a stale map entry can never
  // strand someone on a locale that does not exist. Whole-country picks keep
  // the country default by construction (the sentinel matches no rule).
  const subregionLocale = getSubregionLocale(countryCode, subregion);
  const subregionLocaleIsDeployed =
    !!subregionLocale &&
    countryLanguages[subregionLocale as Locale]?.regionCode === countryCode;
  const defaultLocale =
    subregionLocale && subregionLocaleIsDeployed ? subregionLocale : countryDefaultLocale;
  const label =
    selection.city?.trim() ||
    context.fallbackLabel?.trim() ||
    // Registry-free fallback: the country's own name for a country-wide pick,
    // otherwise the code itself. Every real call site passes a label.
    (isWholeCountryCode(subregion)
      ? getCountryDisplayName(countryCode) ?? countryCode
      : subregion);

  const location: UserLocation = {
    city: label,
    region: subregion,
    country: countryCode,
    ...(selection.coordinates ? { coordinates: selection.coordinates } : {}),
    updatedAt: context.now ?? Date.now(),
    // Written ONLY when the shopper actually stated an ordering. Absent has to
    // keep meaning "never asked / never answered" — stamping the default here
    // would erase the difference between "wants nationwide" and "never said",
    // which is exactly the difference a one-time prompt needs.
    ...(isRegionPriority(selection.regionPriority) ? { regionPriority: selection.regionPriority } : {}),
    regionSource: selection.regionSource ?? 'explicit',
  };

  const locParam = encodeLocationParam(location);

  const queryParams = new URLSearchParams();
  if (context.searchQuery) queryParams.set('searchQuery', context.searchQuery);
  if (context.tutorialMode) queryParams.set('tutorial', 'step2');
  queryParams.set('loc', locParam);
  const queryString = `?${queryParams.toString()}`;

  // Tutorial mode always restarts at the locale root: its choreography owns the
  // next step, and returning mid-flow would drop the shopper out of the tour.
  const targetPath =
    context.returnUrl && !context.tutorialMode
      ? convertPathToLocale(context.returnUrl, defaultLocale)
      : `/${defaultLocale}`;

  // Dev serves every locale from one origin, so the router handles it; prod
  // resolves the region apex (and stays relative on the native-app host).
  const targetUrl = context.isDev
    ? `${targetPath}${queryString}`
    : resolveCrossRegionUrl(
        `${targetPath}${queryString}`,
        countryLanguages[defaultLocale as Locale]?.regionCode,
      );

  return { location, locParam, localeToSet: defaultLocale, targetUrl };
}

/** localStorage value with an expiry, matching the region-prompt writer. */
function setItemWithTTL(key: string, value: string, ttlMinutes: number): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ value, expiry: Date.now() + ttlMinutes * 60 * 1000 }),
    );
  } catch {
    // A full/blocked storage must never abort a region change.
  }
}

/** Cookie posture shared with `locale`: functional, long-lived, first-party. */
const FUNCTIONAL_COOKIE_OPTIONS = {
  expires: SUBREGION_PREF_COOKIE_DAYS,
  path: '/',
  secure: true,
  sameSite: 'lax',
} as const;

/**
 * Mirror the preference into the server-readable cookie. Handing js-cookie the
 * raw `CC:code:priority` is deliberate: its writer escapes what a cookie value
 * may not hold (spaces, accents, apostrophes — 'Provence-Alpes-Côte d'Azur' is
 * a real stored value) and leaves `:` alone, so the segments survive.
 */
export function writeSubregionPrefCookie(pref: SubregionPref): void {
  if (typeof document === 'undefined') return;
  try {
    Cookies.set(SUBREGION_PREF_COOKIE, encodeSubregionPrefCookie(pref), FUNCTIONAL_COOKIE_OPTIONS);
  } catch (error) {
    console.error('Error writing subregion preference cookie:', error);
  }
}

/** Client-side read of the cookie the server reads. `null` ⇒ no preference. */
export function readSubregionPrefCookie(): SubregionPref | null {
  if (typeof document === 'undefined') return null;
  try {
    return parseSubregionPrefCookie(Cookies.get(SUBREGION_PREF_COOKIE));
  } catch {
    return null;
  }
}

/**
 * Persist a region the shopper actually stated: localStorage (the source of
 * truth, which also fires `REGION_SELECTION_CHANGED_EVENT` so the header badge
 * updates live) AND the cookie, so the very next SERVER render already knows.
 *
 * Use this instead of a bare `setUserLocation` anywhere a choice should outlive
 * the session.
 */
export function persistExplicitRegion(location: UserLocation): void {
  // `regionPriority` is NOT defaulted into storage — see `buildSelectionPayload`.
  const stamped: UserLocation = {
    ...location,
    regionSource: location.regionSource ?? 'explicit',
  };
  setUserLocation(stamped);
  // The cookie carries the EFFECTIVE ordering (the server needs an answer, not
  // a maybe); whether the shopper stated it lives in the stored location.
  writeSubregionPrefCookie({
    country: stamped.country,
    code: stamped.region,
    priority: stamped.regionPriority ?? DEFAULT_REGION_PRIORITY,
  });
}

/**
 * THE commit. Side effects in the order the /region page has always run them:
 *
 *   1. persist the location (+ the new preference cookie)
 *   2. suppress the "where are you?" prompt on the target locale for 30 min
 *   3. pin the `locale` cookie (production only — see below)
 *   4. navigate: same-origin via the router, cross-origin via window.location
 *
 * Step 3 stays production-only because that is what shipped: in dev every
 * locale is served from one origin and the router push already lands on the
 * right one, so writing the cookie would only add a way for a dev session to
 * disagree with its own URL.
 */
export function commitRegionSelection(
  selection: RegionSelection,
  context: RegionCommitContext,
): RegionSelectionPayload | null {
  const payload = buildSelectionPayload(selection, context);
  if (!payload) return null;

  persistExplicitRegion(payload.location);
  setItemWithTTL(
    `region-prompt-dismissed-${payload.localeToSet}`,
    'true',
    REGION_PROMPT_DISMISSED_TTL_MINUTES,
  );

  if (!context.isDev) {
    Cookies.set('locale', payload.localeToSet, FUNCTIONAL_COOKIE_OPTIONS);
  }

  if (payload.targetUrl.startsWith('http')) {
    if (typeof window !== 'undefined') window.location.href = payload.targetUrl;
  } else {
    context.push(payload.targetUrl);
  }

  return payload;
}

/**
 * The preference as it travels to the server on a search request.
 *
 * `code` is the stored spelling, NOT necessarily a canonical admin-1 code: the
 * resolver writes canonical codes, but locations stored before it existed (and
 * France/UK, which the frozen legacy tables key by name) can hold a name. The
 * server resolves it through `getSubregion(country, code)`, where the registry
 * is already loaded.
 */
export interface SubregionPrefTransport {
  code: string;
  priority: RegionPriority;
  source: RegionPrefSource;
}

/**
 * PURE. The `subregionPref` body field for a search request, or `undefined`
 * when there is nothing to say.
 *
 * A whole-country choice IS a preference and is transported: "I explicitly want
 * nationwide results" and "I never said" are different facts, and only the
 * first should ever suppress a prompt.
 *
 * `source` defaults to `detected`, never `explicit`: a location stored before
 * provenance was tracked cannot be used as evidence the shopper stated it.
 */
export function buildSubregionPrefField(
  location: UserLocation | null | undefined,
): SubregionPrefTransport | undefined {
  const code = location?.region?.trim();
  if (!code) return undefined;
  return {
    code,
    priority: location?.regionPriority ?? DEFAULT_REGION_PRIORITY,
    source: location?.regionSource ?? 'detected',
  };
}

/**
 * Client-side hydration: the stored location for this country, falling back to
 * the cookie when localStorage has nothing (a fresh cross-origin landing, or a
 * profile whose storage was cleared but whose cookie survived).
 */
export function readSubregionPrefForCountry(
  countryCode: string,
  storedLocation: UserLocation | null | undefined,
): SubregionPrefTransport | undefined {
  const fromLocation = buildSubregionPrefField(storedLocation);
  if (fromLocation) return fromLocation;

  const cookie = readSubregionPrefCookie();
  if (!cookie) return undefined;
  if (cookie.country !== countryCode.trim().toUpperCase()) return undefined;
  // The cookie is only ever written by an explicit persist.
  return { code: cookie.code, priority: cookie.priority, source: 'explicit' };
}
